"use strict";

(function () {
  class NxcAssistantState {
    constructor() {
      this.reset();
    }

    reset() {
      this.selectedUse = this.pendingSelection = null;
      this.currentStep = "USE";
      this.history = [];
    }

    transition(step, buttonId) {
      this.history.push(this.currentStep);
      if (step === "PRICE") this.selectedUse = buttonId;
      if (step === "SELECTED") this.pendingSelection = buttonId;
      this.currentStep = step;
    }

    goBack() {
      this.currentStep = this.history.pop() || "USE";
    }
  }

  class NxcAssistant {
    constructor() {
      this.root = document.querySelector("[data-nxc]");
      this.modal = document.getElementById("helper");
      if (!this.root || !this.modal || !window.helperConfigsUrl) return;

      try {
        this.i18n = JSON.parse(document.getElementById("NeoxAssistantTranslations")?.textContent || "{}");
      } catch (_error) {
        this.i18n = {};
      }
      this.state = new NxcAssistantState();
      this.containers = this.modal.querySelectorAll(".state-container");
      this.backButton = this.modal.querySelector(".back-button");
      this.exampleTexts = this.modal.querySelectorAll(".power-desc");
      this.useButtonsRendered = false;
      this.tierLabelsApplied = false;
      this.initEventListeners();
      this.fetchConfigurations().catch(console.error);
    }

    async fetchConfigurations() {
      if (this.configurations) return;
      const response = await fetch(window.helperConfigsUrl);
      const data = await response.json();
      this.useCases = (data.useCases || []).map((useCase) => ({
        ...useCase,
        label: this.i18n.useCases?.[useCase.id] || useCase.label,
      }));
      const tierLabels = this.i18n.tiers?.[this.i18n.config] || {};
      this.tiers = (data.tiers || []).map((tier) => ({
        ...tier,
        label: tierLabels[tier.id] || tier.label,
      }));
      this.specRows = data.specRows || [];
      this.configurations = data.configurations || {};
      this.descriptions = this.i18n.descriptions || data.descriptions || {};
      this.propertyGroups = data.propertyGroups || null;
      this.directOptions = data.directOptions || null;
      this.renderUseCases();
      this.applyTierLabels();
      this.updateUI();
    }

    renderUseCases() {
      if (this.useButtonsRendered || !this.useCases.length) return;
      const target =
        this.modal.querySelector("#USE .helper-section") ||
        this.modal.querySelector("#use-buttons");
      if (!target) return;

      target.innerHTML = this.useCases.map((useCase) => `
        <div class="col-12 col-lg text-lg-center" draggable="false">
          <button type="button" class="icon-btn" id="${useCase.id}" data-next="PRICE">
            <div class="circular-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" class="mx-auto svg-icon" width="48">
                <path fill="#333333" d="${useCase.iconPath}"/>
              </svg>
            </div>
            <span class="icon-text">${useCase.label}</span>
          </button>
        </div>
      `).join("");
      this.useButtonsRendered = true;
    }

    applyTierLabels() {
      if (this.tierLabelsApplied || !this.tiers.length) return;
      this.tiers.forEach((tier) => {
        const button = this.modal.querySelector(`#${tier.id}`);
        if (!button) return;
        const label = button.querySelector(".icon-text");
        if (label) label.textContent = tier.label;
      });
      this.tierLabelsApplied = true;
    }

    hideAllContainers() {
      this.containers.forEach((container) => {
        container.style.display = "none";
      });
    }

    showContainer(id) {
      const container = this.modal.querySelector(`#${id}`);
      if (container) container.style.display = "block";
    }

    updateExampleText() {
      const desc = this.descriptions && this.descriptions[this.state.selectedUse];
      if (!desc) return;
      this.exampleTexts.forEach((element, index) => {
        element.textContent = desc[index] || "";
      });
    }

    getConfig(useCase, tier) {
      const useConfigs = this.configurations && this.configurations[useCase];
      return useConfigs ? useConfigs[tier] : null;
    }

    // Property → candidate group ids, from the helper config's propertyGroups map
    // (arrays cover gated splits; first visible wins). Inline map = legacy fallback.
    groupIds(propertyName) {
      const mapped = this.propertyGroups
        ? this.propertyGroups[propertyName]
        : {
            Chipset: "platform",
            Threadripper: "cpu-tr",
            "Threadripper PRO": "cpu-pro",
            Motherboard: "motherboard",
            "Graphics Card": "gpu",
            "Additional Graphics (5090)": "gpu-qty-5090",
            "Additional Graphics (6000)": "gpu-qty-6000",
            Memory: "ram",
            Storage: "storage",
            "Power Supply": "psu",
          }[propertyName];
      if (!mapped) return [];
      return Array.isArray(mapped) ? mapped : [mapped];
    }

    optionId(propertyName, value) {
      const valueText = String(value).trim();
      const direct = this.directOptions || {
        Chipset: {
          Threadripper: "tr",
          "Threadripper Pro": "pro",
          "Threadripper PRO": "pro",
        },
        "Additional Graphics (5090)": {
          "1X": "1",
          "2X": "2",
          "1x": "1",
          "2x": "2",
        },
        "Additional Graphics (6000)": {
          "1X": "1",
          "2X": "2",
          "3X": "3",
          "4X": "4",
          "1x": "1",
          "2x": "2",
          "3x": "3",
          "4x": "4",
        },
      };
      return direct[propertyName] && direct[propertyName][valueText];
    }

    normalize(text) {
      return String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    }

    ownOptions(group) {
      const container = Array.from(group.children).find((child) =>
        child.classList && child.classList.contains("nxc-options")
      );
      return container
        ? Array.from(container.children).filter((child) => child.classList && child.classList.contains("nxc-opt"))
        : [];
    }

    findOption(propertyName, value) {
      for (const groupId of this.groupIds(propertyName)) {
        const group = this.root.querySelector(`[data-nxc-group="${groupId}"]`);
        if (!group || group.hidden) continue;

        const exactId = this.optionId(propertyName, value);
        if (exactId) {
          return this.ownOptions(group).find((option) => option.getAttribute("data-nxc-option") === exactId) || null;
        }

        const needle = this.normalize(value);
        const match = this.ownOptions(group).find((option) => {
          const id = this.normalize(option.getAttribute("data-nxc-option"));
          const label = this.normalize(option.querySelector(".nxc-opt__title")?.textContent);
          return id.includes(needle) || label.includes(needle);
        });
        if (match) return match;
      }
      return null;
    }

    optionText(option) {
      return option ? option.querySelector(".nxc-opt__title")?.textContent.trim() || "" : "";
    }

    applyConfiguration(config) {
      Object.entries(config).forEach(([propertyName, value]) => {
        const option = this.findOption(propertyName, value);
        if (option) option.click();
      });
    }

    displayPendingOptions() {
      const config = this.getConfig(this.state.selectedUse, this.state.pendingSelection);
      if (!config) return;

      this.specRows.forEach((row) => {
        const target = this.modal.querySelector(row.specSelector);
        if (!target) return;
        let text = "";

        for (const propertyName of row.propertyNames) {
          const value = config[propertyName];
          if (value == null) continue;
          const option = this.findOption(propertyName, value);
          if (option) {
            text = this.optionText(option);
            break;
          }
        }

        if (!text) return;
        if (row.quantityFrom) {
          for (const quantityProperty of row.quantityFrom) {
            const quantityValue = config[quantityProperty];
            if (quantityValue == null) continue;
            const quantity = parseInt(quantityValue, 10);
            if (quantity > 1) {
              text = `${quantity}x ${text}`;
              break;
            }
          }
        }
        target.innerText = text;
      });
    }

    applySelectedConfiguration() {
      const config = this.getConfig(this.state.selectedUse, this.state.pendingSelection);
      if (!config) return;
      this.applyConfiguration(config);
      this.displayPendingOptions();
    }

    updateUI() {
      this.hideAllContainers();
      if (this.backButton) {
        this.backButton.style.display = this.state.currentStep === "USE" ? "none" : "block";
      }
      if (this.state.currentStep === "PRICE") this.updateExampleText();
      if (this.state.currentStep === "SELECTED") this.applySelectedConfiguration();
      this.showContainer(this.state.currentStep);
    }

    initEventListeners() {
      this.modal.addEventListener("shown.bs.modal", () => {
        this.fetchConfigurations().catch(console.error);
        if (window.neoxTrack) {
          const nxcRoot = document.querySelector("[data-nxc]");
          window.neoxTrack("assistant_opened", { handle: nxcRoot ? nxcRoot.getAttribute("data-nxc-handle") : null });
        }
      });
      this.modal.addEventListener("hidden.bs.modal", () => {
        this.state.reset();
        this.updateUI();
      });

      this.modal.addEventListener("click", (event) => {
        const button = event.target.closest("button");
        if (!button || !this.modal.contains(button)) return;
        if (button.classList.contains("back-button")) {
          this.state.goBack();
        } else {
          const nextState = button.getAttribute("data-next");
          if (nextState) this.state.transition(nextState, button.id);
        }
        this.updateUI();
      });
    }

    async applyConfigFromURL() {
      const configParam = new URLSearchParams(location.search).get("config");
      if (!configParam) return;
      const [useCase, tier] = configParam.split("-");
      if (!useCase || !tier) return;

      await this.fetchConfigurations();
      const config = this.getConfig(useCase, tier);
      if (config) this.applyConfiguration(config);
    }
  }

  const assistant = new NxcAssistant();
  if (assistant.root) assistant.applyConfigFromURL().catch(console.error);
})();
